import { useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { api, ApiError, type EventHole, type HoleMap as HoleMapData } from '../../api/public'
import { Failed } from '../../components/Async'
import { nextPoll } from '../../components/eventState'
import { HoleMap } from '../../components/HoleMap'
import { chosenView, holeYards, type MapPlayer } from '../../components/holeGeometry'
import { LiveLoading, LiveStatus } from '../../components/Live'
import { MapViewSwitch } from '../../components/MapViewSwitch'
import { ShotTable } from '../../components/ShotTable'
import { usePlayerPaths } from '../../components/playerPaths'
import { useApi } from '../../components/useApi'
import { usePolling } from '../../hooks/usePolling'
import { useSwipe } from '../../hooks/useSwipe'

/**
 * One hole of one week: every shot played on it, drawn on the hole's map.
 *
 * # Reached from the week, and from a match
 *
 * The week page links a hole wherever it has shots, and the match grid links
 * each hole with `?match=` so the view shows just that match's two players --
 * the hole the match was won or lost on, as it was played.
 *
 * # Polled like the week it belongs to
 *
 * While somebody is at the simulator the hole keeps asking, on the same cadence
 * as the week page (`nextPoll`), and stops once the week can no longer change.
 * The map itself is course data and is read once.
 */
export function HoleView() {
  const { eventId, hole } = useParams()
  const id = Number(eventId)
  const n = Number(hole)
  const live = usePolling(
    (s) => api.eventHole(id, n, s),
    [id, n],
    (d: EventHole) => nextPoll(d.state),
  )
  const courseKey = live.data?.course_key || null
  const map = useApi(courseKey ? (s: AbortSignal) => api.courseMapHole(courseKey, n, s) : null, [
    courseKey,
    n,
  ])

  if (live.loading) return <LiveLoading what="the hole" />
  if (!live.data)
    return <Failed what="This hole" error={live.error ?? new Error('nothing loaded')} />

  // A course with no map is an ordinary state, not a failure: the shots are
  // still drawn, over plain ground.
  const noMap = map.state === 'error' && map.error instanceof ApiError && map.error.status === 404
  if (map.state === 'error' && !noMap) return <Failed what="This hole's map" error={map.error} />
  const mapData: HoleMapData | null = map.state === 'ready' ? map.data : null
  return (
    <HoleViewBody
      hole={live.data}
      map={mapData}
      mapLoading={map.state === 'loading' && !!courseKey}
      live={live}
    />
  )
}

function HoleViewBody({
  hole,
  map,
  mapLoading,
  live,
}: {
  hole: EventHole
  map: HoleMapData | null
  mapLoading: boolean
  live: ReturnType<typeof usePolling<EventHole>>
}) {
  const [params] = useSearchParams()
  const { resolve, slugFor } = usePlayerPaths()
  const matchId = Number(params.get('match')) || null
  const match = matchId ? hole.matches.find((m) => m.id === matchId) : undefined
  const shown = match
    ? hole.players.filter(
        (p) => p.player_id === match.player_a_id || p.player_id === match.player_b_id,
      )
    : hole.players
  // Colours follow the WHOLE field's order, so a player keeps their colour when
  // the view narrows to one match.
  const players: MapPlayer[] = shown.map((p) => ({
    name: p.display_name,
    series: hole.players.findIndex((q) => q.player_id === p.player_id),
    shots: p.shots,
  }))
  // Whose shots are hidden, in the address (`?hide=dad,mike`) like the photo
  // switch: it survives the polling and a reader can send the view they built.
  // Slugs rather than UUIDs so the address stays readable; either resolves.
  const hidden = new Set(
    (params.get('hide') ?? '')
      .split(',')
      .filter(Boolean)
      .map((h) => resolve(h) ?? h),
  )
  const toggleHref = (playerId: string) => {
    const nextHidden = new Set(hidden)
    if (nextHidden.has(playerId)) nextHidden.delete(playerId)
    else nextHidden.add(playerId)
    const q = new URLSearchParams(params)
    if (nextHidden.size) q.set('hide', [...nextHidden].map((id) => slugFor(id) ?? id).join(','))
    else q.delete('hide')
    const s = q.toString()
    return `/events/${hole.event_id}/holes/${hole.hole}${s ? `?${s}` : ''}`
  }
  const drawn = players.filter((_, i) => !hidden.has(shown[i].player_id))
  const hasAerial = !!map?.views.aerial
  const view = chosenView(params.get('view'), hasAerial)
  const yards = holeYards(map, hole.tee)
  const at = hole.holes.indexOf(hole.hole)
  const step = (to: number | undefined) => {
    if (to === undefined) return undefined
    const next = new URLSearchParams(params)
    return `/events/${hole.event_id}/holes/${to}?${next.toString()}`
  }
  const prev = step(hole.holes[at - 1])
  const next = step(hole.holes[at + 1])
  // Swipe between holes on a phone, the way the previous/next links step.
  const navigate = useNavigate()
  const swipe = useSwipe(
    next ? () => navigate(next, { replace: true }) : null,
    prev ? () => navigate(prev, { replace: true }) : null,
  )
  // And with the arrow keys on a desktop. A key held with a modifier is the
  // browser's (Cmd+Left is "back"), so only a bare arrow steps.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      if (e.key === 'ArrowLeft' && prev) navigate(prev, { replace: true })
      if (e.key === 'ArrowRight' && next) navigate(next, { replace: true })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate, prev, next])
  const anyShots = shown.some((p) => p.shots.length > 0)

  return (
    <section className="hole-view" data-testid="hole-view" {...swipe}>
      <p className="note">
        <Link to={`/events/${hole.event_id}`}>← The week</Link>
      </p>
      <h2>
        Hole {hole.hole}
        {match && shown.length === 2 && (
          <span className="hole-view-match">
            {' '}
            · {shown[0].display_name} v {shown[1].display_name}
          </span>
        )}
      </h2>
      <p className="note hole-facts">
        Par {hole.par} · index {hole.stroke_index}
        {yards !== null && <> · {yards} yd</>} · {hole.tee} tee
        {match && (
          <>
            {' · '}
            <Link to={`/events/${hole.event_id}/holes/${hole.hole}${viewOnly(params)}`}>
              Everyone
            </Link>
          </>
        )}
      </p>

      {live.polling && (
        <LiveStatus loadedAt={live.loadedAt} failures={live.failures} error={live.error} />
      )}

      {/* Each player is a LINK that hides or shows their shots: public views
          carry no buttons (FR-047c), and the choice belongs in the address. */}
      <ul
        className="hole-scores"
        aria-label="Scores on this hole; select a player to hide or show their shots"
      >
        {players.map((p, i) => {
          const off = hidden.has(shown[i].player_id)
          return (
            <li key={p.name} className={`series-${p.series % 5}${off ? ' hole-score-off' : ''}`}>
              <Link
                to={toggleHref(shown[i].player_id)}
                replace
                aria-label={`${off ? 'Show' : 'Hide'} ${p.name}'s shots`}
                data-testid={`toggle-${p.name}`}
              >
                <span className="swatch" aria-hidden="true" />
                {p.name} <b className="num">{shown[i].gross}</b>{' '}
                <span className="note">net {shown[i].net}</span>
              </Link>
            </li>
          )
        })}
        {players.length === 0 && <li className="note">Nobody has a score on this hole yet.</li>}
      </ul>

      {hasAerial && <MapViewSwitch view={view} />}

      {mapLoading ? (
        <p className="state-loading">Loading the map…</p>
      ) : (
        <>
          {!map && (
            <p className="note">
              This course has no map yet, so the shots are drawn over plain ground from the tee.
            </p>
          )}
          {!anyShots && players.length > 0 && (
            <p className="note">No shots were recorded on this hole; the scores are the record.</p>
          )}
          <HoleMap
            map={map}
            view={view}
            tee={hole.tee}
            pin={hole.pin}
            players={drawn}
            label={`Hole ${hole.hole}, par ${hole.par}, with ${drawn.length} players' shots`}
          />
        </>
      )}

      <ShotTable
        pin={hole.pin}
        players={shown
          .map((p, i) => ({
            name: p.display_name,
            series: players[i].series,
            gross: p.gross,
            shots: p.shots,
          }))
          .filter((p) => !hidden.has(shown.find((q) => q.display_name === p.name)!.player_id))}
      />

      <p className="hole-step">
        {prev ? <Link to={prev}>← Hole {hole.holes[at - 1]}</Link> : <span />}
        {next ? <Link to={next}>Hole {hole.holes[at + 1]} →</Link> : <span />}
      </p>
    </section>
  )
}

/** The current query with only the view kept, for "show everyone". */
function viewOnly(params: URLSearchParams): string {
  const v = params.get('view')
  return v ? `?view=${v}` : ''
}
